import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import { getUriFilters, patchObject } from '@webapp/account/src/resources/lib';
import { saveQuestionParam } from '@webapp/account/src/resources/questions';
import type { Answer } from '@webapp/account/src/resources/reports/answers';
import { initialAnswer } from '@webapp/account/src/resources/reports/answers';
import {
    changeCrossTabParam,
    changeLinkParams,
    deleteCrossTab,
    getAnswer,
    getAudioFilesStat,
    getCrossStat,
    getCrossTabList,
    getLinksStat,
    getQuestions,
    getQuestionsForReports,
    getReports,
    getWordStat,
    refreshQuestions,
    removeAnswer,
    removeFilter,
    singleAnswerList,
    startResult,
} from '@webapp/account/src/resources/reports/async';
import { getComparisonReports, getComparisonWordStat } from '@webapp/account/src/resources/reports/async-comparison';
import { getFilterGroups, removeFilterGroup } from '@webapp/account/src/resources/reports/async-filters';
import {
    ALL_TIME_PERIOD_ID,
    BASE_PERIOD_ID,
    NO_COMPARISON_TYPES,
} from '@webapp/account/src/resources/reports/comparison';
import type { FilterSet } from '@webapp/account/src/resources/reports/filter';
import {
    applyFiltersAndMarkSpoiled,
    FilterFieldType,
    FilterType,
    resetAllFilters,
    resetFilterType,
    toggleFilterSet,
} from '@webapp/account/src/resources/reports/filter';
import type { GlobalFilter, IFilterTag } from '@webapp/account/src/resources/reports/global-filters';
import { initialGlobalFilter } from '@webapp/account/src/resources/reports/global-filters';
import { calcReportList, calcReports, updateReportItem } from '@webapp/account/src/resources/reports/lib';
import { mapCrossTab } from '@webapp/account/src/resources/reports/map';
import type { BaseReport, Col, Item, Report, Row } from '@webapp/account/src/resources/reports/reports';
import type { ReportsState } from '@webapp/account/src/resources/reports/state';
import { reportsInitial } from '@webapp/account/src/resources/reports/state';
import { getSurvey } from '@webapp/account/src/resources/survey/async';

import { today } from '@webapp/common/lib/date';
import { getParamsData, mapSurveyFilters } from '@webapp/common/lib/params';
import { updateItemInArray } from '@webapp/common/lib/utils';
import { initialSurveyAddonShare } from '@webapp/common/resources/entities/survey-addons/share';
import { QuestionType } from '@webapp/common/resources/survey';

const comparisonKey = (periodId: string, reportId: number): string => `${periodId}:${reportId}`;

const getComparisonPeriodIds = (state: ReportsState): Array<string> => {
    // В PDF периоды приходят из URI, а не из стора — читаем те же фильтры, что и селектор.
    const filters = getUriFilters() || state.globalFilters;
    const comparePeriods = Array.isArray(filters.comparePeriods) ? filters.comparePeriods : [];

    return comparePeriods.map(({ id }) => id).filter((id) => id !== BASE_PERIOD_ID);
};

const invalidateComparison = (state: ReportsState): void => {
    const questionIds = state.questions.map(({ id }) => id);

    state.comparison.wordStatByPeriod = {};

    getComparisonPeriodIds(state).forEach((periodId) => {
        const reportIds = questionIds.length
            ? questionIds
            : Object.keys(state.comparison.byPeriod[periodId] || {}).map(Number);
        reportIds.forEach((reportId) => state.comparison.stale.add(comparisonKey(periodId, reportId)));
    });

    // Типы без сравнения перезапрашиваем за всё время опроса.
    if (getComparisonPeriodIds(state).length) {
        state.questions.forEach(({ id, type }) => {
            if (NO_COMPARISON_TYPES.has(type)) {
                state.comparison.stale.add(comparisonKey(ALL_TIME_PERIOD_ID, id));
            }
        });
    }
};

// При смене состава периодов чистим ветки удалённых периодов.
const syncComparisonPeriods = (state: ReportsState): void => {
    const ids = new Set(getComparisonPeriodIds(state));

    Object.keys(state.comparison.byPeriod).forEach((periodId) => {
        if (periodId !== ALL_TIME_PERIOD_ID && !ids.has(periodId)) {
            delete state.comparison.byPeriod[periodId];
            delete state.comparison.wordStatByPeriod[periodId];
        }
    });

    [state.comparison.stale, state.comparison.fetching].forEach((set) => {
        Array.from(set).forEach((key) => {
            const periodId = key.split(':')[0];
            if (periodId !== BASE_PERIOD_ID && periodId !== ALL_TIME_PERIOD_ID && !ids.has(periodId)) {
                set.delete(key);
            }
        });
    });
};

const invalidateReports = (state: ReportsState): void => {
    state.reports.forEach(({ id }) => {
        state.staleReports.add(id);
    });
    invalidateComparison(state);
};

const markLoadedReports = (state: ReportsState, reports: Array<Report>): void => {
    reports.forEach((report) => {
        // payload может содержать undefined-элементы — не роняем редьюсер (FRONT-2A9M)
        if (report) {
            state.staleReports.delete(report.id);
        }
    });
};

// TODO rewrite with immer
export const updateOrInsert = (reports: Array<Report>, newReports: Array<Report>): Array<Report> => {
    reports.forEach((r, idx) => {
        if (!r) return;

        const updateIdx = newReports.findIndex((report) => report && report.id === r.id);
        if (updateIdx > -1) {
            const r2 = JSON.parse(JSON.stringify(r));
            const report = newReports.splice(updateIdx, 1)[0];
            if (!isEqual(r2, report)) {
                reports[idx] = report;
            }
        }
    });

    return reports.concat(newReports);
};

export const toggleFilter = <E extends { filterType: FilterType }>(item: E, filterType: FilterType): void => {
    item.filterType = item.filterType === filterType ? null : filterType;
};

export const toggleFilterType = (
    entities: Array<any>,
    id: number,
    filterType: FilterType,
    type?: QuestionType,
    aux?: any,
): any =>
    updateItemInArray(entities, id, (e) => {
        if (type && (type === QuestionType.RESIDENCE || type === QuestionType.NESTED_LIST)) {
            // TODO ?? wtf
            if (e.itemId === aux) {
                toggleFilter(e, filterType);
            }
            return;
        }
        toggleFilter(e, filterType);
    });

const updateGlobalFilters = ({
    state,
    filterType,
    col,
    filterFieldType,
    item,
    itemId,
    reportId,
    row,
    value,
}: {
    state: ReportsState;
    filterType: FilterType;
    col?: any;
    filterFieldType: FilterFieldType;
    item?: any;
    itemId?: number;
    reportId: number;
    row?: any;
    value?: any;
}): void => {
    const { reports } = state;
    const report = reports.find(({ id }) => id === reportId);
    const filters = toggleFilterSet(
        state.globalFilters.filters,
        // TODO: typings
        { col, filterFieldType, item, itemId, row, value } as any,
        report,
        filterType,
    );

    invalidateReports(state);

    switch (filterFieldType) {
        case FilterFieldType.SELF:
            updateItemInArray(reports, reportId, (r: Report) => {
                toggleFilterType(r.items[0].self, itemId, filterType);
            });
            break;
        case FilterFieldType.COMMENT:
            updateItemInArray(reports, reportId, (r: Report) => {
                toggleFilterType(r.comments, itemId, filterType);
            });
            break;
        case FilterFieldType.CATEGORY:
            // Kano category filtering - toggle filterType on matching cell by category
            updateReportItem(reports, { item }, reportId, (i) => {
                i.rows.forEach((r) => {
                    if (r.id === row.id) {
                        r.cells.forEach((c) => {
                            if (c.value?.category === value?.category) {
                                c.filterType = filterType;
                            }
                        });
                    }
                });
                return i;
            });
            break;
        default:
            updateReportItem(reports, { item }, reportId, (i) => {
                if (row && col) {
                    i.rows.forEach((r) => {
                        // remove row\col filters then cell selected
                        r.filterType = null;
                        if (r.id === row.id) {
                            toggleFilterType(r.cells, col.id, filterType);
                        }
                    });
                    i.cols.forEach((c) => {
                        // remove row\col filters then cell selected
                        c.filterType = null;
                    });
                } else if (row) {
                    i.rows.forEach((r) => {
                        r.cells.forEach((c) => {
                            c.filterType = null;
                        });
                    });
                    toggleFilterType(i.rows, row.id, filterType, report.type);
                } else if (col) {
                    i.rows.forEach((r) => {
                        r.cells.forEach((c) => {
                            c.filterType = null;
                        });
                    });
                    toggleFilterType(i.cols, col.id, filterType);
                } else {
                    toggleFilter(i, filterType);
                }
                return i;
            });
    }

    state.globalFilters.filters = filters;
};

const updateTagFilters = ({
    state,
    filterType,
    filterFieldType,
    text,
    reportId,
}: {
    state: ReportsState;
    filterType: FilterType;
    filterFieldType: FilterFieldType;
    text: string;
    reportId: number;
}): void => {
    const { reports } = state;
    const report = reports.find(({ id }) => id === reportId);

    const isIncluded = filterType === FilterType.INCLUDE;
    const reportTagsArray = state.globalFilters.tags[reportId]?.items || [];

    if (isIncluded) {
        state.globalFilters.tags = {
            ...state.globalFilters.tags,
            [reportId]: {
                name: report.name,
                items: state.globalFilters.tags[reportId]?.items.filter((item) => item.type !== FilterType.EXCLUDE),
            },
        };
    }
    const findIndex = reportTagsArray?.findIndex((item) => item.text === text);

    if (findIndex > -1) {
        state.globalFilters.tags[reportId]?.items.splice(findIndex, 1);

        if (!reportTagsArray.length) {
            delete state.globalFilters.tags[reportId];
        }
    } else {
        const newItem: IFilterTag = { text, type: filterType };

        if (filterFieldType === FilterFieldType.COMMENT_CLOUD) newItem.comment = true;

        state.globalFilters.tags = {
            ...state.globalFilters.tags,
            [reportId]: {
                name: report.name,
                items: [...reportTagsArray, newItem],
            },
        };
    }
};

const toggleIds = (ids: Set<number>, id: number): void => {
    if (ids.has(id)) {
        ids.delete(id);
    } else {
        ids.add(id);
    }
};

const adjustLinkParamsFilter = (state: ReportsState, paramId: number, value: string, filterType: FilterType): void => {
    const filters = state.globalFilters;
    const idx = filters.linkParamsFilters.findIndex((f) => f.paramId === paramId && f.value === value);
    const response = (() => {
        const st = state.linkStats.get(paramId);

        return st.responses.find((r) => r.value === value);
    })();

    toggleFilter(response, filterType);

    if (idx > -1) {
        if (filters.linkParamsFilters[idx].filterType === filterType) {
            filters.linkParamsFilters.splice(idx, 1);
        } else {
            toggleFilter(filters.linkParamsFilters[idx], filterType);
        }
    } else {
        filters.linkParamsFilters.push({ paramId, value, filterType });
    }
};

const findFilterSet = (state, row) => {
    if (!row) return false;

    return state.filterGroup?.filters?.find((filter) => {
        return filter.sets.find((set) => set.id === row?.id);
    });
};

export const reportsSlice = createSlice({
    name: 'reports',
    initialState: reportsInitial,
    reducers: {
        singleAnswerListSetPage(state, { payload: page }: PayloadAction<number>) {
            state.answersPage = page;
            state.fetchAnswers = true;
        },
        setFiltersVisible(state, { payload: visible }: PayloadAction<boolean>) {
            state.filtersVisible = visible;
        },
        patchFilters(state, { payload: patch }: PayloadAction<Partial<GlobalFilter>>) {
            patchObject(state.globalFilters, patch);
            syncComparisonPeriods(state);
            invalidateReports(state);
        },
        // Переименование периода не меняет выборку, поэтому данные не инвалидируем.
        renameComparisonPeriod(state, { payload }: PayloadAction<{ id: string; name: string }>) {
            const name = payload.name.trim();

            if (payload.id === BASE_PERIOD_ID) {
                state.globalFilters.basePeriodName = name || undefined;
                return;
            }

            const period = (state.globalFilters.comparePeriods || []).find(({ id }) => id === payload.id);

            if (period) {
                period.name = name || undefined;
            }
        },
        toggleExcludeQuestion(state, { payload: { questionId } }) {
            toggleIds(state.globalFilters.excludedQuestionIds, questionId);
            invalidateReports(state);
        },
        toggleExcludedParam(state, { payload: { paramId } }) {
            toggleIds(state.globalFilters.excludedParamIds, paramId);
            invalidateReports(state);
        },
        toggleLinkParamsFilter(state, { payload: { filterType, paramId, value } }) {
            if (
                state.filterGroup?.linkParamsFilters?.find((link) => link.paramId === paramId && link.value === value)
            ) {
                return;
            }
            adjustLinkParamsFilter(state, paramId, value, filterType);
            invalidateReports(state);
        },
        excludeReportResult(
            state,
            {
                payload: { col, filterFieldType, item, itemId, reportId, row, value },
            }: {
                payload: {
                    reportId: number;
                    item: Item;
                    row?: Row;
                    col?: Col;
                    filterFieldType?: FilterFieldType;
                    itemId?: number;
                    value?: any;
                };
            },
        ) {
            if (findFilterSet(state, row)) return;

            updateGlobalFilters({
                state,
                filterType: FilterType.EXCLUDE,
                col,
                filterFieldType,
                item,
                itemId,
                reportId,
                row,
                value,
            });
            invalidateReports(state);
        },
        removeParamFilter(state, { payload }: { payload: { paramId: number; value: string } }) {
            const { paramId, value } = payload;

            const idx = state.globalFilters.linkParamsFilters.findIndex(
                (f) => f.paramId === paramId && f.value === value,
            );

            if (idx > -1) {
                state.globalFilters.linkParamsFilters.splice(idx, 1);
            }

            invalidateReports(state);
        },
        resetFilters(state, { payload: { surveyCreatedDate } }) {
            resetAllFilters(state.reports);
            invalidateReports(state);
            state.globalFilters = {
                ...cloneDeep(initialGlobalFilter),
                dateTo: today,
                dateFrom: surveyCreatedDate,
            };
        },
        updateAllFilters(state, { payload }) {
            resetAllFilters(state.reports);
            state.globalFilters = mapSurveyFilters(payload);
            syncComparisonPeriods(state);
            invalidateReports(state);
        },
        includeReportResult(
            state,
            {
                payload: { col, filterFieldType, item, itemId, reportId, row, value },
            }: {
                payload: {
                    reportId: number;
                    item: Item;
                    row?: Row;
                    col?: Col;
                    filterFieldType?: FilterFieldType;
                    itemId?: number;
                    value?: any;
                };
            },
        ) {
            if (findFilterSet(state, row)) return;

            updateGlobalFilters({
                state,
                filterType: FilterType.INCLUDE,
                col,
                filterFieldType,
                item,
                itemId,
                reportId,
                row,
                value,
            });
            invalidateReports(state);
        },
        toggleFilterWordCloudTag(
            state,
            {
                payload: { reportId, text, filterType, filterFieldType },
            }: {
                payload: {
                    reportId: number;
                    text: string;
                    filterType: FilterType;
                    filterFieldType?: FilterFieldType;
                };
            },
        ) {
            if (state.filterGroup?.tags[reportId]?.items.find((item) => item.text === text)) {
                return;
            }

            updateTagFilters({
                state,
                filterType,
                filterFieldType,
                reportId,
                text,
            });
            invalidateReports(state);
        },
        toggleCrossTabFilters(state, { payload: { id } }) {
            toggleIds(state.globalFilters.excludedCrossTabsId, id);

            invalidateReports(state);
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getQuestionsForReports.pending, (state, { meta: { arg } }) => {
            const { globalFilters } = arg;
            state.globalFilters = globalFilters;
            state.fetchReports = true;
        });
        builder.addCase(getQuestionsForReports.fulfilled, (state, { payload: reports }) => {
            state.fetchReports = false;
            state.questions = reports;
            markLoadedReports(state, reports);
        });
        builder.addCase(getQuestionsForReports.rejected, (state) => {
            state.fetchReports = false;
        });
        builder.addCase(getQuestions.fulfilled, (state, { payload }) => {
            state.questions = payload;
        });
        builder.addCase(getWordStat.fulfilled, (state, { payload }) => {
            state.wordStat = payload;
        });
        builder.addCase(deleteCrossTab.fulfilled, (state, { payload }) => {
            state.crossTabs = state.crossTabs.filter((item) => item.id !== payload.tabId);
        });
        builder.addCase(getCrossTabList.fulfilled, (state, { payload }) => {
            state.crossTabs = payload.map(mapCrossTab);
            state.isCrossTabsLoaded = true;
        });
        builder.addCase(changeCrossTabParam.fulfilled, (state, { payload }) => {
            state.crossTabs = payload;
        });
        builder.addCase(getCrossStat.fulfilled, (state, { payload: { tabId, stat } }) => {
            state.crossTabsStat.set(tabId, stat);
        });
        builder.addCase(removeFilter.pending, (state, { meta: { arg } }) => {
            const { filter, set } = arg;
            const { id, sets } = filter;

            if (sets.length === 1) {
                // remove filter if last set
                state.globalFilters.filters = state.globalFilters.filters.filter((f) => f.id !== id);
            } else {
                updateItemInArray(state.globalFilters.filters, id, (f: BaseReport) => {
                    const idx = f.sets.findIndex(
                        (s) => s.id === set.id && s.itemId === set.itemId && s.direction === set.direction,
                    );
                    f.sets = f.sets.filter((set, i) => i !== idx);
                });
            }

            if (set.direction) {
                updateReportItem(state.reports, { set: set }, filter?.id, (i: Item) => {
                    if (set.direction === FilterFieldType.ROW) {
                        resetFilterType<Row>(i.rows, i, filter, set, FilterFieldType.ROW);
                    } else if (set.direction === FilterFieldType.COL) {
                        resetFilterType<Col>(i.cols, i, filter, set, FilterFieldType.COL);
                    } else {
                        i.filterType = null;
                    }
                });
            } else {
                updateItemInArray(state.reports, id, (r: Report) => {
                    const appliedSets = sets.filter((s: FilterSet) => s.type !== null && s.direction === null);
                    r.items.forEach((i) => {
                        if (i.id === set.id && i.id === set.itemId) {
                            i.filterType = null;
                        }
                        if (appliedSets.length === 1) {
                            i.filterType = null;
                        }
                    });
                });
            }

            invalidateReports(state);
        });
        builder.addCase(getReports.pending, (state, { meta: { arg } }) => {
            const { globalFilters, upsertQuestions } = arg;

            state.globalFilters = globalFilters;
            state.fetchReports = !upsertQuestions;
        });
        builder.addCase(getReports.rejected, (state) => {
            state.fetchReports = false;
        });
        builder.addCase(refreshQuestions.fulfilled, (state, { payload }) => {
            state.reports = state.reports.map((item) => {
                const newReport = payload[item.id];

                // вопроса может уже не быть в свежем payload — оставляем отчёт как есть
                if (!newReport) return item;

                const result = {
                    ...item,
                    name: newReport.name,
                    question: Object.assign(item.question, newReport.question),
                };

                result.items = result.items.map((item) => {
                    const newItem = { ...item };

                    if (newReport.question?.rows?.length) {
                        newItem.rows = newItem.rows.map((row) => {
                            const findNewRow = newReport.question.rows.find((newRow: Row) => newRow.id === row.id);

                            if (findNewRow) {
                                return {
                                    ...row,
                                    value1: findNewRow.value,
                                    value2: findNewRow.value2,
                                    value3: findNewRow.value3,
                                };
                            }

                            const findNewRowInCols = newReport.question.cols.find(
                                (newCol: Col) => newCol.id === row.id,
                            );

                            if (findNewRowInCols) {
                                return {
                                    ...row,
                                    value1: findNewRowInCols.name,
                                };
                            }
                        });
                    }

                    return newItem;
                });

                return result;
            });
        });
        builder.addCase(
            getReports.fulfilled,
            (state, { payload: { field, filesCount, filesSize, inItems, page = 1, reports, upsertQuestions } }) => {
                const loadMore = page > 1 || field === 'group';

                // TODO use immer
                const mappedReports = applyFiltersAndMarkSpoiled(
                    reports,
                    state.globalFilters.filters,
                    state.questions,
                    upsertQuestions,
                );

                markLoadedReports(state, mappedReports);

                if (loadMore) {
                    state.reports.forEach((r, idx) => {
                        if (!r) return;

                        const mr = mappedReports.find((report) => report && report.id === r.id);

                        if (mr) {
                            if (field === 'comments') {
                                r.comments = r.comments.concat(mr.comments);
                                r.commentsPage = page;
                                return;
                            }

                            if (field === 'group') {
                                state.reports[idx] = {
                                    ...mr,
                                    responsesPage: r.responsesPage,
                                    commentsPage: r.commentsPage,
                                };
                                return;
                            }

                            if (inItems) {
                                mr.items = r.items.concat(mr.items);
                                mr.responsesPage = page;
                                state.reports[idx] = mr;
                                return;
                            }

                            if (field === 'responses') {
                                r.items.forEach((i, idx) => {
                                    i.self = i.self.concat(mr.items[idx].self);
                                });
                                r.responsesPage = page;
                                return;
                            }

                            mr.items.forEach((i, idx) => {
                                // TODO remove [stat, resp] from matrix
                                i.rows = [...r.items[idx].rows, ...i.rows];
                            });

                            mr.responsesPage = page;
                            state.reports[idx] = mr;
                        }
                    });
                }

                if (!loadMore) {
                    // Первая страница: запоминаем её для последующих "Показать ещё"
                    // (см. FRONT-74 — страница больше не выводится из длины списка).
                    mappedReports.forEach((r) => {
                        if (field === 'comments') {
                            r.commentsPage = page;
                        } else if (field !== 'group') {
                            r.responsesPage = page;
                            if (!field) {
                                r.commentsPage = page;
                            }
                        }
                    });
                }

                const newReports = loadMore ? state.reports : mappedReports;

                state.reports = upsertQuestions ? updateOrInsert(state.reports, newReports) : newReports;
                state.filesSize = filesSize;
                state.filesCount = filesCount;
                state.fetchReports = false;
                state.reportsLoaded = true;
                calcReports(state);
            },
        );
        builder.addCase(getComparisonReports.pending, (state, { meta: { arg } }) => {
            const { reportIds } = arg;

            getComparisonPeriodIds(state).forEach((periodId) => {
                reportIds.forEach((reportId) => state.comparison.fetching.add(comparisonKey(periodId, reportId)));
            });
        });
        builder.addCase(getComparisonReports.rejected, (state, { meta: { arg } }) => {
            const { reportIds } = arg;

            getComparisonPeriodIds(state).forEach((periodId) => {
                reportIds.forEach((reportId) => state.comparison.fetching.delete(comparisonKey(periodId, reportId)));
            });
        });
        builder.addCase(getComparisonReports.fulfilled, (state, { payload }) => {
            const activeIds = new Set(getComparisonPeriodIds(state));

            payload.forEach(({ periodId, reports }) => {
                if (periodId !== ALL_TIME_PERIOD_ID && !activeIds.has(periodId)) {
                    return;
                }

                const mappedReports = applyFiltersAndMarkSpoiled(
                    reports,
                    state.globalFilters.filters,
                    state.questions,
                    undefined,
                );

                // Проценты/средние/CSAT считаются на фронте, как и для базового периода.
                calcReportList(mappedReports);

                if (!state.comparison.byPeriod[periodId]) {
                    state.comparison.byPeriod[periodId] = {};
                }

                mappedReports.forEach((report) => {
                    state.comparison.byPeriod[periodId][report.id] = report;
                    state.comparison.stale.delete(comparisonKey(periodId, report.id));
                    state.comparison.fetching.delete(comparisonKey(periodId, report.id));
                });
            });
        });
        builder.addCase(getComparisonWordStat.fulfilled, (state, { payload }) => {
            payload.forEach(({ periodId, stat }) => {
                state.comparison.wordStatByPeriod[periodId] = stat;
            });
        });
        builder.addCase(singleAnswerList.pending, (state, { meta: { arg } }) => {
            const { globalFilters } = arg;
            state.globalFilters = globalFilters;
            state.fetchAnswers = true;
        });
        builder.addCase(singleAnswerList.rejected, (state) => {
            state.fetchAnswers = false;
        });
        builder.addCase(singleAnswerList.fulfilled, (state, { payload: { answers, answersTotalCount, page } }) => {
            state.answers = answers;
            state.fetchAnswers = false;
            state.answersTotalCount = answersTotalCount;
            state.answersPage = page;
        });
        builder.addCase(getAnswer.pending, (state) => {
            state.answer.fetch = true;
        });
        builder.addCase(getAnswer.rejected, (state) => {
            state.answer.fetch = false;
        });
        builder.addCase(getAnswer.fulfilled, (state, { payload: { answer, nearbyAnswers } }) => {
            state.answer = {
                ...cloneDeep(initialAnswer),
                ...answer,
                fetch: false,
            };
            state.nearbyAnswers = nearbyAnswers;
        });
        builder.addCase(removeAnswer.pending, (state, { meta: { arg } }) => {
            const { answerId } = arg;
            updateItemInArray(state.answers, answerId, (a: Answer) => {
                a.fetch = true;
            });
        });
        builder.addCase(removeAnswer.rejected, (state, { meta: { arg } }) => {
            const { answerId } = arg;

            updateItemInArray(state.answers, answerId, (a: Answer) => {
                a.fetch = false;
            });
        });
        builder.addCase(removeAnswer.fulfilled, (state, { meta: { arg } }) => {
            const { answerId } = arg;

            state.answersTotalCount -= 1;
            state.answers = state.answers.filter((a) => a.id !== answerId);
        });
        builder.addCase(startResult.pending, (state) => {
            state.fetchPublic = true;
        });
        builder.addCase(startResult.rejected, (state) => {
            state.share = cloneDeep(initialSurveyAddonShare);
            state.fetchPublic = false;
        });
        builder.addCase(
            startResult.fulfilled,
            (state, { payload: { createdDatetime, endDatetime, share, passwordAccess, filterGroup } }) => {
                if (passwordAccess) return;

                state.share = share;
                state.fetchPublic = false;
                state.hideFilters = !share.filters;

                if (filterGroup) {
                    const newFilterGroup = JSON.parse(filterGroup);

                    newFilterGroup.filters.forEach((filter) => {
                        filter.sets.forEach((set) => {
                            set.isDisabled = true;
                        });
                    });

                    newFilterGroup.linkParamsFilters.forEach((link) => {
                        link.isDisabled = true;
                    });

                    Object.keys(newFilterGroup.tags).forEach((id) => {
                        newFilterGroup.tags[id].items.forEach((item) => (item.isDisabled = true));
                    });

                    state.filterGroup = newFilterGroup;
                    state.globalFilters = mapSurveyFilters(newFilterGroup);
                } else {
                    state.globalFilters.dateFrom = createdDatetime;
                    // Максимальная дата публичного отчёта — сегодня, а не конец лицензии.
                    state.globalFilters.dateTo = endDatetime && endDatetime > today ? today : endDatetime || today;
                }

                syncComparisonPeriods(state);
            },
        );
        builder.addCase(getSurvey.fulfilled, (state, { payload: { survey } }) => {
            state.globalFilters = cloneDeep(survey.addons.filters);
            syncComparisonPeriods(state);
            calcReports(state);
            state.share = cloneDeep(survey.addons.share);
        });
        builder.addCase(getLinksStat.fulfilled, (state, { payload }) => {
            const { data, page } = payload;

            if (page === 1) {
                state.linkStats = new Map();
            }

            data.forEach((st) => {
                const target = state.linkStats.get(st.id);

                if (target) {
                    target.responses = target.responses.concat(st.responses);
                } else {
                    state.linkStats.set(st.id, st);
                }
            });
        });
        builder.addCase(saveQuestionParam.pending, ({ questions, reports }, { meta: { arg } }): void => {
            const { field, questionId, value, params } = arg;

            updateItemInArray(questions, questionId, ({ question }: Report) => {
                if (question?.params) {
                    Object.assign(question.params, getParamsData({ field, value, params }));
                }
            });
            updateItemInArray(reports, questionId, ({ question }: Report) => {
                if (question?.params) {
                    Object.assign(question.params, getParamsData({ field, value, params }));
                }
            });
        });

        builder.addCase(changeLinkParams.fulfilled, ({ linkStats }, { payload: { id, params } }) => {
            const prev = linkStats.get(id);

            linkStats.set(id, { ...prev, params });
        });
        builder.addCase(getAudioFilesStat.fulfilled, (state, { payload: { count, size } }) => {
            state.audioCount = count;
            state.audioSize = size;
        });
        builder.addCase(getFilterGroups.fulfilled, (state, { payload: { rows }, meta: { arg } }) => {
            state.filterGroups.list = rows.map(
                ({
                    DATE_CREATE,
                    FILTERS_DATA,
                    ID,
                    NAME,
                }: {
                    DATE_CREATE: string;
                    FILTERS_DATA: string;
                    ID: number;
                    NAME: string;
                }) => ({
                    dateCreated: DATE_CREATE,
                    data: JSON.parse(FILTERS_DATA),
                    id: ID,
                    name: NAME,
                }),
            );
            state.filterGroups.surveyId = arg?.surveyId;
        });
        builder.addCase(removeFilterGroup.fulfilled, (state, { meta: { arg } }) => {
            state.filterGroups.list = state.filterGroups.list.filter((item) => item.id !== arg);
        });
    },
});
